import { useTranslation } from 'react-i18next';
import { Tabs, tabPanelProps } from '@platform/ui';
import { ArticleEditTab } from './article/ArticleEditTab';
import { ArticleReadTab } from './article/ArticleReadTab';
import { isArticleType } from './article/articleToc';
import { AssignmentsTab } from './AssignmentsTab';
import { ChildRecordsTab } from './ChildRecordsTab';
import { defaultTabOf, tabKeysOf } from './childRelations';
import { CommentsTab } from './CommentsTab';
import { FilesTab } from './FilesTab';
import { HistoryTab } from './HistoryTab';
import type { IRecordTabsProps } from './IRecordTabsProps';
import { LawDesignerTab } from './lawdesigner/LawDesignerTab';
import { isLawTab } from './lawdesigner/lawTabs';
import { RelatedItemsTab } from './RelatedItemsTab';
import { useChildRecords } from './useChildRecords';
import { visualizationOf } from './visualization/visualizationOf';
import { VisualizationTab } from './visualization/VisualizationTab';
import './tabs.css';

/** Основа ідентифікаторів вкладок запису (вкладка `rec-tab-tab-<ключ>`, панель `rec-tab-panel`; UIUX controls/tabs, WAI-ARIA tabs). */
const TAB_IDS = 'rec-tab';

/**
 * Вкладки запису (UIUX forms/tabs, прототип v4): [Структура · Зацікавлені сторони · Інтереси · Відносини · Аналітичні записи · Правки —
 * візуальна модель закону] [Візуалізація / Брифінг — для записів-діаграм] [Читання · Редагувати — для статей] Загальне · Доручення · Дочірні завдання ·
 * Пов'язані елементи · Коментарі · Файли · Історія — набір з form.tabs визначення (дочірні — за ієрархією типу); лічильник після
 * підпису — пігулка mono, показується й нуль. Без вкладки в адресі — типова (defaultTabOf: «Структура», «Візуалізація», інакше «Загальне» —
 * і для статті, як у прототипі; новий запис — «Загальне», інші вкладки нового запису підказують спершу зберегти його).
 */
export function RecordTabs({ ws, module, rt, record, editor, active, onChange, children, isNew = false }: IRecordTabsProps) {
  const { t } = useTranslation();
  const kids = useChildRecords(ws, rt, record);
  const counts = record.counts;
  const count: Record<string, number | undefined> = {
    assignments: counts?.assignments ?? 0, children: kids.total, related: counts?.links ?? 0, comments: counts?.comments ?? 0,
    files: counts?.files ?? 0, history: counts?.history ?? 0,
  };
  const article = isArticleType(rt);
  const viz = visualizationOf(module, rt, record);
  const keys = tabKeysOf(rt.form.tabs, Boolean(kids.parent), [...(article ? ['read', 'edit'] : []), ...(viz ? ['visualization'] : [])]);
  const label = (k: string) => (k === 'children' && rt.key === 'task' ? t('recordPage.tabs.childTasks')
    : k === 'visualization' && viz === 'briefing' ? t('recordPage.tabs.briefing') : t(`recordPage.tabs.${k}`));
  const tab = keys.includes(active) ? active : isNew ? 'general' : defaultTabOf(rt, Boolean(viz));
  const later = isNew && !['general', 'read', 'edit'].includes(tab);
  const articleProps = { ws, moduleKey: module.module.key, rt, record, editor, onEdit: () => onChange('edit') };
  return (
    <div className="rtab">
      <Tabs tabs={keys.map((k) => ({ key: k, label: label(k), count: count[k] }))} active={tab} onChange={onChange} idBase={TAB_IDS} />
      <div className="rtab-panel" {...tabPanelProps(TAB_IDS, tab)}>
        {!later && isLawTab(tab) && <LawDesignerTab ws={ws} module={module} rt={rt} record={record} tab={tab} />}
        {!later && tab === 'visualization' && viz && <VisualizationTab ws={ws} module={module} rt={rt} record={record} kind={viz} />}
        {tab === 'read' && <ArticleReadTab {...articleProps} />}
        {tab === 'edit' && <ArticleEditTab {...articleProps} />}
        {tab === 'general' && children}
        {later && <p className="rtab-empty rtab-empty--draft">{t('recordPage.draft.tabHint')}</p>}
        {!later && tab === 'assignments' && <AssignmentsTab ws={ws} record={record} />}
        {!later && tab === 'children' && <ChildRecordsTab ws={ws} module={module} rt={rt} record={record} />}
        {!later && tab === 'related' && <RelatedItemsTab ws={ws} module={module} record={record} />}
        {!later && tab === 'comments' && <CommentsTab ws={ws} record={record} />}
        {!later && tab === 'files' && <FilesTab ws={ws} record={record} />}
        {!later && tab === 'history' && <HistoryTab ws={ws} rt={rt} record={record} />}
      </div>
    </div>
  );
}
